import React, { useRef, useState } from 'react'
import { type LexicalEditor } from 'lexical'
import { $generateHtmlFromNodes } from '@lexical/html'

import { Modal } from '../../../../components/Modal/Modal'
import { WritableLexicalEditor } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { RichTextToolbarPlugin } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { LEXICAL_NODES } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'

import { type CrmActionModalProps } from '../crmActionRegistry'

import { useResolvedResults } from './useResolvedResults'
import { useWealthboxEligibility } from './useWealthboxEligibility'
import { useWealthboxActionSave } from './useWealthboxActionSave'
import { WealthboxEligibilityNotice, WealthboxLoadingBody, WealthboxNoEligibleAlert } from './WealthboxEligibilityFeedback'

import styles from './WealthboxNoteActionModal.module.scss'

export const WealthboxNoteActionModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults }) => {
  const { results, resolving } = useResolvedResults(resolveResults)
  const eligibility = useWealthboxEligibility(results ?? [])

  const { save, saving } = useWealthboxActionSave({
    eligibility,
    onClose,
    sourceObject: 'note',
    entityType: 'notes',
    relatedToType: 'Contact',
    successToast: { title: 'Adding note', message: 'Wealthbox note creation started.' },
    errorToast: { title: 'Failed to add note', message: 'Could not start the Wealthbox note creation. Please try again.' },
  })

  const editorRef = useRef<LexicalEditor>(null)
  const [textContent, setTextContent] = useState('')

  const count = results?.length ?? 0
  const targetLabel = `${count} ${count === 1 ? 'client' : 'clients'}`

  const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

  const body =
    resolving || eligibility.status === 'loading' ? (
      <WealthboxLoadingBody />
    ) : noEligibleClients ? (
      <WealthboxNoEligibleAlert />
    ) : (
      <div className='d-flex flex-column gap-4'>
        <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
        <WritableLexicalEditor
          ref={editorRef}
          className={`p-0 ${styles.editor}`}
          editorContainerClassName='flex-grow-1 pt-2 px-3 pb-4 position-relative'
          onTextContentChange={setTextContent}
          placeholder={
            <div className='paragraph-medium zocks-text-gray-700 px-3 pt-2 position-absolute top-0 start-0 pe-none'>Enter a note for selected clients…</div>
          }
          lexicalNodes={LEXICAL_NODES}
          theme={{}}
          header={<RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2' />}
        />
        <WealthboxEligibilityNotice eligibility={eligibility} verbPhrase='add a note to' />
      </div>
    )

  const handleSave = async () => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    const html = editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
    await save({ note: html })
  }

  const primaryActionDisabled = noEligibleClients
    ? false
    : resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0 || textContent.trim().length === 0

  return (
    <Modal
      show={open}
      onHide={onClose}
      header={<h4>Add note to {targetLabel}</h4>}
      body={body}
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={noEligibleClients ? onClose : handleSave}
      footerPrimaryActionTitle={noEligibleClients ? 'Done' : 'Add note'}
      footerPrimaryActionDisabled={primaryActionDisabled}
      footerPrimaryActionLoading={saving}
      onFooterSecondaryAction={onClose}
      hideSecondaryAction={noEligibleClients}
      centered={true}
    />
  )
}
